export const CALENDAR_PROMPT_VARIABLES = ['current_day', 'current_date', 'current_time', 'greeting'];

export const CALENDAR_PROMPT_SNIPPET = [
  'CURRENT_DAY      : "{{current_day}}"',
  'CURRENT_DATE     : "{{current_date}}"',
  'CURRENT_TIME     : "{{current_time}}"',
].join('\n');

export const GREETING_PROMPT_SNIPPET = '{{greeting}}';

// Conditional/helper block tokens consumed by the prompt renderer (registry.py):
//   {{#if VarName}} ... {{/if}}, {{#unless VarName}} ... {{/unless}},
//   {{#eq ...}} ... {{/eq}}, and {{#neq ...}} ... {{/neq}}
// These are valid prompt syntax — not malformed {{variable}} references. Strip
// them out before running the malformed-variable matcher so tokens that abut
// bare words (e.g. `{{#if X}}text{{/if}}`) don't produce false fragments.
export const CONDITIONAL_OPEN_REGEX = /\{\{#(?:if|unless)\s+([a-zA-Z_][a-zA-Z0-9_ ]*?)\}\}/g;
export const HELPER_BLOCK_TOKEN_REGEX = /\{\{#(?:if|unless)\s+[a-zA-Z_][a-zA-Z0-9_ ]*\}\}|\{\{#(?:eq|neq)\s+[^{}]*\}\}|\{\{\/(?:if|unless|eq|neq)\}\}/g;

export function stripConditionalBlockTokens(text) {
  return String(text || '').replace(HELPER_BLOCK_TOKEN_REGEX, '');
}

// A malformed variable is only an *unbalanced* brace count around an identifier
// — `{{name}` or `{name}}` — i.e. an unambiguous typo of `{{name}}`. Braces show
// up in prompts for plenty of legitimate reasons that are not variable attempts:
// JSON tool-payload examples (`{"ended": true}`, `{}`), code snippets, regex
// quantifiers. Single-brace `{name}` is also left alone — it is far more often
// a placeholder in example text than a broken variable. Only flag what we can
// be sure about; the undeclared-variable warning covers the rest.
const BRACED_TOKEN_REGEX = /(\{+)\s*([a-zA-Z_][a-zA-Z0-9_ ]{0,49}?)\s*(\}+)/g;

export function findMalformedPromptVariables(text) {
  if (!text) return [];

  // Conditional block tokens ({{#if X}}…{{/if}}) are valid syntax — remove them
  // first so they (and any bare words they abut) aren't flagged.
  const scanText = stripConditionalBlockTokens(text);
  const regex = new RegExp(BRACED_TOKEN_REGEX.source, 'g');
  const issues = [];
  let match;

  while ((match = regex.exec(scanText)) !== null) {
    const [pattern, openBraces, varName, closeBraces] = match;
    if (openBraces.length === closeBraces.length) continue;
    issues.push({ pattern, varName: varName.trim() || 'var' });
  }

  return issues;
}

export function normalizePromptVariableName(value) {
  return String(value || '').trim().toLowerCase();
}

export function isBuiltInPromptVariable(value) {
  return CALENDAR_PROMPT_VARIABLES.includes(normalizePromptVariableName(value));
}

export function filterUserCallInfields(fields = []) {
  return (Array.isArray(fields) ? fields : []).filter(
    (field) => !isBuiltInPromptVariable(field?.fieldName)
  );
}

export function extractPromptVariables(text) {
  if (!text) return [];

  const matches = [];
  const regex = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_ ]{0,49})\s*\}\}/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    matches.push(normalizePromptVariableName(match[1]));
  }

  // Also treat the guard variable of a conditional block ({{#if X}} / {{#unless X}})
  // as "used", so a variable referenced only inside a conditional is not reported
  // as undeclared.
  let cond;
  while ((cond = CONDITIONAL_OPEN_REGEX.exec(text)) !== null) {
    matches.push(normalizePromptVariableName(cond[1]));
  }
  CONDITIONAL_OPEN_REGEX.lastIndex = 0;

  return [...new Set(matches.filter(Boolean))];
}

export function promptUsesCalendarVariables(...texts) {
  return texts.some((text) =>
    extractPromptVariables(text).some((variableName) => isBuiltInPromptVariable(variableName))
  );
}

export function getPromptMentionOptions(fields = [], sentences = []) {
  const sentenceOptions = (Array.isArray(sentences) ? sentences : [])
    .filter((item) => String(item?.name || '').trim())
    .map((item) => ({
      key: `sentence:${String(item.name).trim()}`,
      label: String(item.name).trim(),
      kind: 'sentence',
    }));

  return [
    {
      key: '__calendar__',
      label: 'Default Calendar',
      kind: 'calendar',
      insertText: CALENDAR_PROMPT_SNIPPET,
    },
    {
      key: '__greeting__',
      label: 'Default Greeting',
      kind: 'calendar',
      description: 'Good Morning / Good Afternoon / Good Evening',
      insertText: GREETING_PROMPT_SNIPPET,
    },
    ...filterUserCallInfields(fields)
      .filter((field) => String(field?.fieldName || '').trim())
      .map((field) => ({
        key: String(field.fieldName).trim(),
        label: String(field.fieldName).trim(),
        kind: 'field',
      })),
    ...sentenceOptions,
  ];
}
